import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api'; 
import { FaBriefcase } from 'react-icons/fa';
import { X } from 'lucide-react'; // Import X
import styles from './GoogleBusinessAutocomplete.module.css';

const GoogleBusinessAutocomplete = ({ value, onSelect, placeholder, disabled, onChange }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);

    // Sync external value changes
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleClear = (e) => {
        e.stopPropagation();
        setInputValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        if (onChange) onChange('');
        if (onSelect) onSelect(null);
    };

    const handleInputChange = async (e) => {
        const val = e.target.value;
        setInputValue(val);
        
        // Notify parent of text change immediately (like a normal input)
        if (onChange) {
            onChange(val); 
        }

        if (val.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            // Call our new backend proxy endpoint
            const data = await api.get(`/api/GooglePlaces/business-autocomplete?input=${encodeURIComponent(val)}`);
            
            if (data && data.predictions) {
                setSuggestions(data.predictions);
                setShowSuggestions(true);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error("Business search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSelectSuggestion = (item) => {
        const businessName = item.structured_formatting.main_text;
        
        setInputValue(businessName);
        setShowSuggestions(false);

        // Notify parent of selection
        if (onSelect) {
            onSelect({
                businessName: businessName,
                googlePlaceId: item.place_id,
                fullDescription: item.description
            });
        }
    };

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.inputContainer}>
                {/* Use the same icon as AuthPages usually does, or allow customization */}
                <FaBriefcase className={styles.inputIcon} size={16} />
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    disabled={disabled}
                    placeholder={placeholder || "Search for your business..."}
                    className={styles.input}
                    autoComplete="off"
                />
                {inputValue && !disabled && (
                    <button type="button" className={styles.clearBtn} onClick={handleClear}>
                        <X size={16} />
                    </button>
                )}
            </div>
            
            {showSuggestions && suggestions.length > 0 && (
                <ul className={styles.suggestionsList}>
                    {suggestions.map((item) => (
                        <li key={item.place_id} onClick={() => handleSelectSuggestion(item)} className={styles.suggestionItem}>
                            <FaBriefcase size={14} style={{marginRight: '12px', marginTop: '4px', color: '#64748B', flexShrink: 0}}/>
                            <div className={styles.suggestionText}>
                                <span className={styles.mainText}>{item.structured_formatting.main_text}</span>
                                <span className={styles.secondaryText}>{item.structured_formatting.secondary_text}</span>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default GoogleBusinessAutocomplete;